import { TONES } from "../../components/filters/statusColors";
import { useTranslation } from "react-i18next";
import { m } from "framer-motion";
import clsx from "clsx";
import { Lock } from "lucide-react";
import { NAV_ICONS } from "../icons/navIcons";
import { isKeyField } from "../views/cardUtils";

export function DetailField({
  icon,
  label,
  value,
  highlight = false,
  emphasizeValue = false,
  onClick,
  canEdit = true,
  locked = false,
  lockedTooltip,
  isDeactivated = false,
  deactivatedTooltip,
  warningText,
  warningTooltip,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
  /** Accent-coloured value, for the field a reader looks for first (email). */
  emphasizeValue?: boolean;
  onClick?: () => void;
  canEdit?: boolean;
  /**
   * The value is set by the system, not by anyone here — a generated code, or
   * a field owned by another record. Shows a padlock in place of the pencil so
   * it reads as deliberate rather than as a missing permission. Fields that
   * are merely uneditable in the current state leave this off.
   */
  locked?: boolean;
  lockedTooltip?: string;
  /**
   * The saved value points at a record that has since been deactivated. The
   * value still shows — it is what this contract actually references — with a
   * chip marking it, since it can no longer be picked for anything new.
   */
  isDeactivated?: boolean;
  deactivatedTooltip?: string;
  /**
   * A non-fatal problem with the saved value — it exists and is active, but
   * disagrees with something else on the record. Amber, to read differently
   * from the red "deactivated" chip.
   */
  warningText?: string;
  warningTooltip?: string;
}) {
  const { t } = useTranslation("common");
  const Pencil = NAV_ICONS.edit;

  return (
    <div className="detail-field-cq h-full border-b border-[rgb(var(--border))]/60 last:border-b-0">
      <m.div
        onClick={onClick}
        // The row's one tooltip, drawn by GlobalTooltips. Almost every one of
        // these rows is also wrapped in an EditableField that titles its own
        // wrapper "Click to edit"; naming the row here (or blanking it when it
        // cannot be edited) keeps that ancestor from answering as well, and
        // leaves the inner tooltips (the padlock, the deactivated chip) to
        // answer for themselves.
        title={canEdit ? t("editableField.clickToEdit") : ""}
        className={clsx(
          // A fixed three-column partition — label, value, affordance — rather
          // than `justify-between`, which sized every value chip to its own text
          // and left the column edges ragged down the card. The label track is a
          // constant width (not content-sized: each row is its own grid, so
          // content sizing would put every chip at a different offset), which
          // leaves the whole remainder of the row to the value. Below `sm` there
          // isn't room for that, so the label takes the slack instead.
          // Column layout lives in index.css (.detail-field): it responds to
          // this field's own width through a container query, so a field in a
          // narrow column stacks even on a wide screen.
          "detail-field group relative grid h-full items-center",
          "px-2 py-2.5 rounded-lg transition-colors",
          canEdit ? "cursor-pointer" : "cursor-default",
          // Flat rows split by hairlines; only an editable row tints on hover.
          canEdit && "hover:bg-[rgb(var(--card-muted))]",
          highlight && "ring-2 ring-[rgb(var(--text-accent))]",
        )}
        animate={highlight ? { scale: [1, 1.02, 1] } : {}}
        transition={{ duration: 0.35 }}
      >
        <div className="flex min-w-0 items-center gap-2 text-sm text-[rgb(var(--text-muted))]">
          {icon}
          <span className="truncate">{label}</span>
        </div>

        <div className="detail-field__value flex min-w-0 items-center justify-end gap-2">
          {!isDeactivated && warningText && (
            <span
              title={warningTooltip}
              className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${TONES.attention.bg} ${TONES.attention.text}`}
            >
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full bg-amber-500"
              />
              {warningText}
            </span>
          )}

          {isDeactivated && (
            <span
              title={
                deactivatedTooltip ?? t("detailField.deactivatedTooltip")
              }
              className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${TONES.danger.bg} ${TONES.danger.text}`}
            >
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full bg-red-500"
              />
              {t("detailField.deactivated")}
            </span>
          )}
          {/* The value sits on the card surface while the row stays muted, so
            the two read as label and data rather than one flat block. Both
            tones come from existing theme tokens, so the pairing inverts
            correctly in dark themes instead of being hard-coded white. It
            fills the whole value track so every chip in a card lines up. */}
          <div
            className={clsx(
              "detail-field__value-text min-w-0 flex-1 break-words",
              emphasizeValue
                ? "font-semibold text-[rgb(var(--text-accent))]"
                : isKeyField(label) && value && value !== "-"
                  ? "text-base font-semibold text-[rgb(var(--text-primary))]"
                  : "font-medium text-[rgb(var(--text-primary))]",
            )}
          >
            {value ?? "—"}
          </div>
        </div>

        {/* Its own column, so a locked row and an editable one keep the same
          value width instead of the icon eating into one of them. */}
        <div className="detail-field__affordance flex w-4 items-center justify-end">
          {canEdit && (
            <Pencil
              size={14}
              className="text-[rgb(var(--text-accent))] opacity-0 transition group-hover:opacity-100 [@media(hover:none)]:opacity-60"
            />
          )}

          {!canEdit && locked && (
            <span
              title={lockedTooltip ?? t("detailField.locked")}
              className="text-[rgb(var(--text-muted))]"
            >
              <Lock size={14} aria-hidden="true" />
            </span>
          )}
        </div>
      </m.div>
    </div>
  );
}
